function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class SwiperComponent extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    if (typeof Swiper !== 'undefined') {
      this.initSwiper();
    } else {
      setTimeout(() => this.initSwiper(), 100);
    }
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  initSwiper() {
    if (this.swiper) return;

    const loop = this.hasAttribute('loop');
    const speed = parseInt(this.getAttribute('speed')) || 500;
    const effect = this.getAttribute('effect') || 'slide';
    const autoplay = this.hasAttribute('autoplay');
    const isSlideshowV2 = this.dataset.slideshowV2 === 'true';
    const isProductGallery = this.classList.contains('product-media-swiper');
    
    const config = {
      loop: loop,
      speed: speed,
      effect: effect,
      
      navigation: isSlideshowV2 ? false : {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      
      a11y: {
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
      },
      
      watchSlidesProgress: true,
      watchSlidesVisibility: true,
      
      on: {
        init: () => {
          this.dispatchEvent(new CustomEvent('swiper:init', { 
            detail: { swiper: this.swiper } 
          }));
          
          // Slideshow v2 is now handled by inline script with Kursor.js
        },
        slideChange: () => {
          this.dispatchEvent(new CustomEvent('swiper:slideChange', { 
            detail: { 
              swiper: this.swiper, 
              activeIndex: this.swiper.activeIndex,
              realIndex: this.swiper.realIndex 
            } 
          }));
        }
      }
    };
    
    // Configure touch handling for product gallery to allow pinch-zoom and vertical scrolling
    if (isProductGallery && ('ontouchstart' in window || navigator.maxTouchPoints > 0)) {
      config.touchEventsTarget = 'container';
      config.touchAngle = 45; 
      config.threshold = 10; 
      config.shortSwipes = true;
      config.longSwipes = true;
      config.longSwipesRatio = 0.5;
      config.longSwipesMs = 300;
    }

    // Configure for slideshow-v2
    if (isSlideshowV2) {
      const desktopSnap = this.dataset.desktopSnap === 'true';
      const mobileFreeScroll = this.dataset.mobileFreeScroll === 'true';
      
      config.slidesPerView = 'auto';
      config.spaceBetween = 0;
      config.centeredSlides = false;
      config.grabCursor = true;
      config.allowTouchMove = true;
      config.simulateTouch = true; 
      config.touchRatio = 1; 
      config.touchAngle = 45;
      // Improve sensitivity for narrow images
      config.threshold = 5;
      config.longSwipesRatio = 0.1;
      config.longSwipesMs = 100;
      config.shortSwipes = true; 
      
      if (window.innerWidth >= 750) {
        // Desktop
        if (desktopSnap) {
          config.freeMode = false;
        } else {
          config.freeMode = {
            enabled: true,
            sticky: false,
            momentum: true,
            momentumRatio: 0.6,
            momentumVelocityRatio: 0.6,
            momentumBounceRatio: 0.7,
            minimumVelocity: 0.02,
          };
          config.resistance = true;
          config.resistanceRatio = 0.85;
        }
        config.mousewheel = {
          enabled: true,
          forceToAxis: true,
        };
      } else {
        // Mobile  
        if (mobileFreeScroll) {
          config.freeMode = {
            enabled: true,
            sticky: false,
            momentum: true,
            momentumRatio: 0.6,
            momentumVelocityRatio: 0.6,
            momentumBounceRatio: 0.7,
            minimumVelocity: 0.02,
          };
          config.resistance = true;
          config.resistanceRatio = 0.85;
        } else {
          config.freeMode = false;
        }
      }
    } else {
      const customGap = this.dataset.spaceBetween != null ? parseFloat(this.dataset.spaceBetween) : null;
      // Default breakpoints for regular swiper
      config.breakpoints = {
        320: {
          slidesPerView: 1,
          spaceBetween: customGap != null ? customGap : 10
        },
        768: {
          slidesPerView: 1,
          spaceBetween: customGap != null ? customGap : 15
        },
        1024: {
          slidesPerView: 1,
          spaceBetween: customGap != null ? customGap : 20
        }
      };
    }

    if (autoplay) {
      const delay = parseInt(this.getAttribute('autoplay-delay')) || 3000;
      config.autoplay = {
        delay: delay,
        disableOnInteraction: false,
      };
    }

    this.swiper = new Swiper(this, config);
  }

  slideNext() {
    if (this.swiper) this.swiper.slideNext();
  }

  slidePrev() {
    if (this.swiper) this.swiper.slidePrev();
  }

  slideTo(index) {
    if (this.swiper) this.swiper.slideTo(index);
  }

  update() {
    if (this.swiper) this.swiper.update();
  }



}

customElements.define('swiper-component', SwiperComponent);

// Slideshow V2 Custom Cursor Component
class SlideshowV2Component extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.cursorSvg = null;
    this.navigationArea = null;
    this.isMouseInside = false;
    this.currentCursorType = null;
    
    // Bind methods
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.swiperComponent = this.querySelector('swiper-component');
    this.prevButton = this.querySelector('.slideshow-v2__nav-button--prev');
    this.nextButton = this.querySelector('.slideshow-v2__nav-button--next');
    
    if (!this.prevButton || !this.nextButton || !this.swiperComponent) return;

    this.createCustomCursor();
    
    this.setupEventListeners();
    
    this.swiperComponent.addEventListener('swiper:init', (e) => {
      this.swiper = e.detail.swiper;
    });

    this.findSwiperWithRetry();
  }

  findSwiperWithRetry(attempts = 0) {
    const maxAttempts = 10;
    const delay = 200;

    if (attempts >= maxAttempts) {
      return;
    }

    if (this.swiper) {
      return;
    }

    // Try different ways to find swiper
    if (this.swiperComponent?.swiper) {
      this.swiper = this.swiperComponent.swiper;
      return;
    }

    const swiperEl = this.querySelector('.swiper') || this.querySelector('.slideshow-v2__swiper');
    if (swiperEl?.swiper) {
      this.swiper = swiperEl.swiper;
      return;
    }

    // Try again after delay
    setTimeout(() => {
      this.findSwiperWithRetry(attempts + 1);
    }, delay);
  }

  createCustomCursor() {
    // Get the cursor arrow size from CSS custom property
    const arrowSize = getComputedStyle(this).getPropertyValue('--cursor-arrow-size') || '1.0';
    const sizeMultiplier = parseFloat(arrowSize);
    const baseSize = 24;
    const actualSize = Math.round(baseSize * sizeMultiplier);
    
    // Create cursor container
    this.cursorContainer = document.createElement('div');
    this.cursorContainer.className = 'slideshow-v2__custom-cursor';
    this.cursorContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: ${actualSize}px;
      height: ${actualSize}px;
      pointer-events: none;
      z-index: 9999;
      opacity: 0;
      transform-origin: center center;
    `;

    // Create SVG element
    this.cursorSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.cursorSvg.setAttribute('width', actualSize);
    this.cursorSvg.setAttribute('height', actualSize);
    this.cursorSvg.setAttribute('viewBox', '0 0 8 7.7');
    this.cursorSvg.setAttribute('fill', 'none');
    this.cursorSvg.style.cssText = `
      width: 100%;
      height: 100%;
    `;

    // Create path elements for left and right arrows using your custom SVG
    this.leftArrowPath = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.leftArrowPath.innerHTML = `
      <polygon points="2.9 0 4.2 0 1.6 3.4 8 3.4 8 4.4 1.6 4.4 4.2 7.7 2.9 7.7 0 3.9 2.9 0" fill="white" stroke="white" stroke-width="0"/>
    `;

    this.rightArrowPath = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.rightArrowPath.innerHTML = `
      <polygon points="5.1 0 3.8 0 6.4 3.4 0 3.4 0 4.4 6.4 4.4 3.8 7.7 5.1 7.7 8 3.9 5.1 0" fill="white" stroke="white" stroke-width="0"/>
    `;

    this.cursorSvg.appendChild(this.leftArrowPath);
    this.cursorSvg.appendChild(this.rightArrowPath);
    this.cursorContainer.appendChild(this.cursorSvg);
    document.body.appendChild(this.cursorContainer);

    // Initially hide both arrows
    this.leftArrowPath.style.display = 'none';
    this.rightArrowPath.style.display = 'none';
  }

  setupEventListeners() {
    // Previous button events
    this.prevButton.addEventListener('mouseenter', (e) => this.handleMouseEnter(e, 'prev'));
    this.prevButton.addEventListener('mouseleave', this.handleMouseLeave);
    this.prevButton.addEventListener('mousemove', (e) => this.handleMouseMove(e, 'prev'));
    this.prevButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleClick(e, 'prev');
    });

    // Next button events
    this.nextButton.addEventListener('mouseenter', (e) => this.handleMouseEnter(e, 'next'));
    this.nextButton.addEventListener('mouseleave', this.handleMouseLeave);
    this.nextButton.addEventListener('mousemove', (e) => this.handleMouseMove(e, 'next'));
    this.nextButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleClick(e, 'next');
    });
  }

  handleMouseEnter(e, buttonType) {
    this.isMouseInside = true;
    this.cursorContainer.style.opacity = '1';
    document.body.style.cursor = 'none';
    
    // Set cursor type based on which button we're hovering
    this.currentCursorType = buttonType;
    this.updateCursorArrow(buttonType);
  }

  handleMouseLeave(e) {
    this.isMouseInside = false;
    this.cursorContainer.style.opacity = '0';
    document.body.style.cursor = 'auto';
    this.currentCursorType = null;
  }

  handleMouseMove(e, buttonType) {
    if (!this.isMouseInside) return;

    const arrowSize = getComputedStyle(this).getPropertyValue('--cursor-arrow-size') || '1.0';
    const sizeMultiplier = parseFloat(arrowSize);
    const baseSize = 24;
    const actualSize = Math.round(baseSize * sizeMultiplier);
    const offset = actualSize / 2;
    
    const x = e.clientX - offset; 
    const y = e.clientY - offset;
    
    requestAnimationFrame(() => {
      this.cursorContainer.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    });


    if (buttonType !== this.currentCursorType) {
      this.updateCursorArrow(buttonType);
      this.currentCursorType = buttonType;
    }
  }

  updateCursorArrow(type) {
    if (type === 'prev') {
      this.leftArrowPath.style.display = 'block';
      this.rightArrowPath.style.display = 'none';
    } else {
      this.leftArrowPath.style.display = 'none';
      this.rightArrowPath.style.display = 'block';
    }
  }

  handleClick(e, buttonType) {
    e.preventDefault();
    
    if (buttonType === 'prev') {
      this.slidePrev();
    } else {
      this.slideNext();
    }
  }

  slidePrev() {
    // Try multiple methods to get swiper
    let swiper = this.swiper || this.swiperComponent?.swiper;
    
    if (swiper && typeof swiper.slidePrev === 'function') {
      swiper.slidePrev();
    } else if (this.swiperComponent && typeof this.swiperComponent.slidePrev === 'function') {
      this.swiperComponent.slidePrev();
    } else {
      // Fallback: try to find and trigger swiper manually
      this.triggerSwiperFallback('prev');
    }
  }

  slideNext() {
    // Try multiple methods to get swiper
    let swiper = this.swiper || this.swiperComponent?.swiper;
    
    if (swiper && typeof swiper.slideNext === 'function') {
      swiper.slideNext();
    } else if (this.swiperComponent && typeof this.swiperComponent.slideNext === 'function') {
      this.swiperComponent.slideNext();
    } else {
      // Fallback: try to find and trigger swiper manually
      this.triggerSwiperFallback('next');
    }
  }

  triggerSwiperFallback(direction) {
    // Last resort: try to find swiper in DOM and trigger manually
    const swiperEl = this.querySelector('.swiper') || this.querySelector('.slideshow-v2__swiper');
    if (swiperEl && swiperEl.swiper) {
      if (direction === 'prev') {
        swiperEl.swiper.slidePrev();
      } else {
        swiperEl.swiper.slideNext();
      }
    }
  }

  cleanup() {
    // Remove event listeners from buttons
    if (this.prevButton) {
      this.prevButton.removeEventListener('mouseenter', this.handleMouseEnter);
      this.prevButton.removeEventListener('mouseleave', this.handleMouseLeave);
      this.prevButton.removeEventListener('mousemove', this.handleMouseMove);
      this.prevButton.removeEventListener('click', this.handleClick);
    }

    if (this.nextButton) {
      this.nextButton.removeEventListener('mouseenter', this.handleMouseEnter);
      this.nextButton.removeEventListener('mouseleave', this.handleMouseLeave);
      this.nextButton.removeEventListener('mousemove', this.handleMouseMove);
      this.nextButton.removeEventListener('click', this.handleClick);
    }

    // Remove cursor from DOM
    if (this.cursorContainer && this.cursorContainer.parentNode) {
      this.cursorContainer.parentNode.removeChild(this.cursorContainer);
    }

    // Reset body cursor
    document.body.style.cursor = 'auto';
  }
}

customElements.define('slideshow-v2-component', SlideshowV2Component);

// Slideshow Embla Component
class SlideshowEmblaComponent extends HTMLElement {
  constructor() {
    super();
    this.embla = null;
    this.cursorSvg = null;
    this.navigationArea = null;
    this.isMouseInside = false;
    this.currentCursorType = null;
    this.dotContainer = null;
    this.dotButtons = null;
    this.dotCleanupFns = [];
    this.boundOnEmblaSelect = this.onEmblaSelect.bind(this);

    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.emblaRoot = this.querySelector('.embla');
    this.emblaContainer = this.querySelector('.embla__container');
    this.emblaSlides = this.querySelectorAll('.embla__slide');
    this.prevButton = this.querySelector('.slideshow-embla__nav-button--prev');
    this.nextButton = this.querySelector('.slideshow-embla__nav-button--next');
    
    // Check if split mode is enabled (2 or fewer blocks with setting enabled)
    this.isSplitMode = this.dataset.splitMode === 'true';
    this.blockCount = parseInt(this.dataset.blockCount) || 0;
    
    if (!this.emblaRoot) return;
    
    // Setup resize handler to handle desktop/mobile switching
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
    
    // Initial setup
    this.setupBasedOnViewport();
  }

  setupBasedOnViewport() {
    const isDesktop = window.innerWidth >= 721;
    const shouldSkipSlider = this.isSplitMode && this.blockCount <= 2 && isDesktop;

    if (shouldSkipSlider) {
      this.teardownDots();
      if (this.embla) {
        this.embla.off('select', this.boundOnEmblaSelect);
        this.embla.destroy();
        this.embla = null;
      }
      return;
    }
    
    if (isDesktop && (!this.prevButton || !this.nextButton)) return;

    if (isDesktop && !this.cursorContainer) {
      this.createCustomCursor();
      this.setupEventListeners();
    }
    
    this.teardownDots();
    if (this.embla) {
      this.embla.off('select', this.boundOnEmblaSelect);
      this.embla.destroy();
      this.embla = null;
    }
    
    if (this.slidesDuplicated && isDesktop) {
      this.removeDuplicatedSlides();
      this.slidesDuplicated = false;
    }
    
    this.initEmbla();
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.setupBasedOnViewport();
    }, 150);
  }

  initEmbla() {
    if (typeof EmblaCarousel === 'undefined') {
      this.loadEmblaCarousel();
      return;
    }

    const loop = this.dataset.loop === 'true';
    const desktopSnap = this.dataset.desktopSnap === 'true';
    const mobileFreeScroll = this.dataset.mobileFreeScroll === 'true';
    const isMobile = window.innerWidth < 750;
    const blockCount = this.blockCount || this.emblaSlides.length;

    const autoMode = this.dataset.autoMode || 'none';
    const autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
    const autoplayMobileOnly = this.dataset.autoplayMobileOnly === 'true';
    const splitSkipsSlider = this.isSplitMode && this.blockCount <= 2 && window.innerWidth >= 721;
    const shouldAutoplay =
      autoMode === 'auto_play' &&
      blockCount > 1 &&
      !splitSkipsSlider &&
      (!autoplayMobileOnly || isMobile);

    if (shouldAutoplay && typeof EmblaCarouselAutoplay === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/embla-carousel-autoplay@8/embla-carousel-autoplay.umd.js';
      script.onload = () => this.initEmbla();
      script.onerror = () => this.initEmbla();
      document.head.appendChild(script);
      return;
    }

    if (isMobile && blockCount === 2 && loop && !this.slidesDuplicated) {
      this.duplicateSlidesForLoop();
      this.slidesDuplicated = true;
    }

    let dragFree = false;
    if (isMobile) {
      dragFree = mobileFreeScroll;
    } else {
      dragFree = !desktopSnap;
    }

    const options = {
      loop: loop,
      align: 'start',
      containScroll: 'trimSnaps',
      dragFree: dragFree,
      slidesToScroll: 1,
      skipSnaps: false,
      duration: 20,
      inViewThreshold: 0.7
    };

    const plugins = [];
    if (shouldAutoplay && typeof EmblaCarouselAutoplay !== 'undefined') {
      plugins.push(
        EmblaCarouselAutoplay({
          delay: autoplaySpeed,
          stopOnMouseEnter: true,
          stopOnInteraction: true,
          playOnInit: true
        })
      );
    }

    this.teardownDots();
    if (this.embla) {
      this.embla.off('select', this.boundOnEmblaSelect);
      this.embla.destroy();
      this.embla = null;
    }

    this.embla = EmblaCarousel(this.emblaRoot, options, plugins);

    this.slideCount = blockCount;

    this.embla.on('select', this.boundOnEmblaSelect);
    this.setupDots();
  }

  onEmblaSelect() {
    if (!this.embla || !this.dotContainer || !this.dotButtons?.length) return;
    const raw = this.embla.selectedScrollSnap();
    const idx = this.slidesDuplicated ? raw % this.blockCount : raw;
    this.dotButtons.forEach((btn, i) => {
      btn.classList.toggle('is-active', i === idx);
    });
  }

  setupDots() {
    this.dotContainer = this.querySelector('[data-slideshow-dots]');
    if (!this.dotContainer || !this.embla) return;
    this.dotButtons = this.dotContainer.querySelectorAll('.slideshow-embla__dot');
    this.dotButtons.forEach((btn, i) => {
      const fn = (e) => {
        e.preventDefault();
        if (!this.embla) return;
        this.embla.scrollTo(i);
      };
      btn.addEventListener('click', fn);
      this.dotCleanupFns.push({ btn, fn });
    });
    this.onEmblaSelect();
  }

  teardownDots() {
    this.dotCleanupFns.forEach(({ btn, fn }) => btn.removeEventListener('click', fn));
    this.dotCleanupFns = [];
    this.dotButtons = null;
    this.dotContainer = null;
  }

  duplicateSlidesForLoop() {
    if (!this.emblaContainer || !this.emblaSlides || this.emblaSlides.length !== 2) {
      return;
    }

    const slidesArray = Array.from(this.emblaSlides);
    slidesArray.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.classList.remove('slideshow-embla__slide--first');
      clone.classList.add('embla__slide--duplicated');
      this.emblaContainer.appendChild(clone);
    });

    this.emblaSlides = this.querySelectorAll('.embla__slide');
  }

  removeDuplicatedSlides() {
    if (!this.emblaContainer) return;
    
    const duplicatedSlides = this.emblaContainer.querySelectorAll('.embla__slide--duplicated');
    duplicatedSlides.forEach((slide) => {
      slide.remove();
    });
    
    this.emblaSlides = this.querySelectorAll('.embla__slide');
  }

  loadEmblaCarousel() {
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/embla-carousel@8/embla-carousel.umd.js';
    script.onload = () => {
      setTimeout(() => {
        if (typeof EmblaCarousel !== 'undefined') {
          this.initEmbla();
        }
      }, 100);
    };
    script.onerror = () => {
      console.error('Failed to load Embla Carousel from CDN');
    };
    document.head.appendChild(script);
  }

  createCustomCursor() {

    const arrowSize = getComputedStyle(this).getPropertyValue('--cursor-arrow-size') || '1.0';
    const sizeMultiplier = parseFloat(arrowSize);
    const baseSize = 24;
    const actualSize = Math.round(baseSize * sizeMultiplier);
    
    this.cursorContainer = document.createElement('div');
    this.cursorContainer.className = 'slideshow-embla__custom-cursor';
    this.cursorContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: ${actualSize}px;
      height: ${actualSize}px;
      pointer-events: none;
      z-index: 9999;
      opacity: 0;
      transform-origin: center center;
    `;

    this.cursorSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.cursorSvg.setAttribute('width', actualSize);
    this.cursorSvg.setAttribute('height', actualSize);
    this.cursorSvg.setAttribute('viewBox', '0 0 8 7.7');
    this.cursorSvg.setAttribute('fill', 'none');
    this.cursorSvg.style.cssText = `
      width: 100%;
      height: 100%;
    `;

    this.leftArrowPath = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.leftArrowPath.innerHTML = `
      <polygon points="2.9 0 4.2 0 1.6 3.4 8 3.4 8 4.4 1.6 4.4 4.2 7.7 2.9 7.7 0 3.9 2.9 0" fill="white" stroke="white" stroke-width="0"/>
    `;

    this.rightArrowPath = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.rightArrowPath.innerHTML = `
      <polygon points="5.1 0 3.8 0 6.4 3.4 0 3.4 0 4.4 6.4 4.4 3.8 7.7 5.1 7.7 8 3.9 5.1 0" fill="white" stroke="white" stroke-width="0"/>
    `;

    this.cursorSvg.appendChild(this.leftArrowPath);
    this.cursorSvg.appendChild(this.rightArrowPath);
    this.cursorContainer.appendChild(this.cursorSvg);
    document.body.appendChild(this.cursorContainer);

    this.leftArrowPath.style.display = 'none';
    this.rightArrowPath.style.display = 'none';
  }

  setupEventListeners() {
    this.prevButton.addEventListener('mouseenter', (e) => this.handleMouseEnter(e, 'prev'));
    this.prevButton.addEventListener('mouseleave', this.handleMouseLeave);
    this.prevButton.addEventListener('mousemove', (e) => this.handleMouseMove(e, 'prev'));
    this.prevButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleClick(e, 'prev');
    });

    this.nextButton.addEventListener('mouseenter', (e) => this.handleMouseEnter(e, 'next'));
    this.nextButton.addEventListener('mouseleave', this.handleMouseLeave);
    this.nextButton.addEventListener('mousemove', (e) => this.handleMouseMove(e, 'next'));
    this.nextButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleClick(e, 'next');
    });
  }

  handleMouseEnter(e, buttonType) {
    this.isMouseInside = true;
    this.cursorContainer.style.opacity = '1';
    document.body.style.cursor = 'none';
    e.currentTarget.style.cursor = 'none';
    
    this.currentCursorType = buttonType;
    this.updateCursorArrow(buttonType);
  }

  handleMouseLeave(e) {
    this.isMouseInside = false;
    this.cursorContainer.style.opacity = '0';
    document.body.style.cursor = 'auto';
    this.currentCursorType = null;
  }

  handleMouseMove(e, buttonType) {
    if (!this.isMouseInside) return;

    const arrowSize = getComputedStyle(this).getPropertyValue('--cursor-arrow-size') || '1.0';
    const sizeMultiplier = parseFloat(arrowSize);
    const baseSize = 24;
    const actualSize = Math.round(baseSize * sizeMultiplier);
    const offset = actualSize / 2;
    
    const x = e.clientX - offset; 
    const y = e.clientY - offset;
    
    requestAnimationFrame(() => {
      this.cursorContainer.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    });

    if (buttonType !== this.currentCursorType) {
      this.updateCursorArrow(buttonType);
      this.currentCursorType = buttonType;
    }
  }

  updateCursorArrow(type) {
    if (type === 'prev') {
      this.leftArrowPath.style.display = 'block';
      this.rightArrowPath.style.display = 'none';
    } else {
      this.leftArrowPath.style.display = 'none';
      this.rightArrowPath.style.display = 'block';
    }
  }

  handleClick(e, buttonType) {
    e.preventDefault();
    
    if (!this.embla) return;
    
    const currentIndex = this.embla.selectedScrollSnap();
    
    if (buttonType === 'prev') {
      const prevIndex = currentIndex === 0 ? this.slideCount - 1 : currentIndex - 1;
      this.embla.scrollTo(prevIndex);
    } else {
      const nextIndex = currentIndex === this.slideCount - 1 ? 0 : currentIndex + 1;
      this.embla.scrollTo(nextIndex);
    }
  }

  cleanup() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
    
    // Clear resize timeout if exists
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.teardownDots();

    // Clean up duplicated slides
    if (this.slidesDuplicated) {
      this.removeDuplicatedSlides();
      this.slidesDuplicated = false;
    }

    if (this.embla) {
      this.embla.off('select', this.boundOnEmblaSelect);
      this.embla.destroy();
      this.embla = null;
    }


    if (this.prevButton) {
      this.prevButton.removeEventListener('mouseenter', this.handleMouseEnter);
      this.prevButton.removeEventListener('mouseleave', this.handleMouseLeave);
      this.prevButton.removeEventListener('mousemove', this.handleMouseMove);
      this.prevButton.removeEventListener('click', this.handleClick);
    }

    if (this.nextButton) {
      this.nextButton.removeEventListener('mouseenter', this.handleMouseEnter);
      this.nextButton.removeEventListener('mouseleave', this.handleMouseLeave);
      this.nextButton.removeEventListener('mousemove', this.handleMouseMove);
      this.nextButton.removeEventListener('click', this.handleClick);
    }


    if (this.cursorContainer && this.cursorContainer.parentNode) {
      this.cursorContainer.parentNode.removeChild(this.cursorContainer);
    }


    document.body.style.cursor = 'auto';
  }
}

customElements.define('slideshow-embla-component', SlideshowEmblaComponent);